import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {logo_tops, logo_project} from '../../assets/images'
import {useTranslation} from '../../utils/useTranslation'
import {useGetMe} from '../../hooks/user.hooks'
import {AuthService} from '../../service/auth.services'
import Icon from '../icon'
import Separator from '../separator'
import {useGetClients} from '../../hooks/client.hooks'
import {useIsMobile} from '../../hooks/responsive.hooks'
import CalendarHeader from '../calendarHeader'
import TodayButton from '../TodayButton'
import SectorFilter from '../SectorFilter'
import LinearWindowFilter from '../LinearWindowFilter'
import {TLinearView} from '../../hooks/networkWindow.hooks'
import {findSettingsEntry} from '../../screens/project/settings/sections'
import ShiftFilter, {ShiftFilterBase} from '../ShiftFilter'
import DashboardPeriodPicker from '../DashboardPeriodPicker'
import CONFIG from '../../constants/config'
import './styles.css'
import ViewModeSwitch from '../ViewModeSwitch'
import ButtonGroup from '../buttonGoup'
import IconButton from '../iconButton'
import {theme} from '../../theme'
import {useListDateMode} from '../../store'
import {useListPeriodPicker} from '../../hooks/listPeriod.hooks'
import {useDashboardHeaderFilters} from '../../hooks/dashboard.hooks'

/**
 * Project sub-paths that carry a time context, so the calendar header is shown
 * in the global header on these screens. Settings is intentionally excluded.
 */
const HEADER_CALENDAR_SUB_PATHS = new Set([
  'work',
  'synoptic',
  'activities',
  'notices',
  'reports',
  'protections',
  'resource-calendar',
])

const Header = () => {
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const user = useGetMe()
  const {data} = useGetClients()
  const {clientId, networkId} = useParams()
  const isMobile = useIsMobile()

  const currentSubPath = pathname.split('/').pop() ?? ''

  const isInNetwork = !!clientId && !!networkId

  // Settings is a NESTED route ("settings/sectors"), so its last segment is the
  // section, not "settings". Anything that must recognise the screen as a whole
  // — or send the user back to the exact same place in another network — reads
  // the full sub-path, never only its tail.
  const networkSubPath = networkId
    ? (pathname.split(`/network/${networkId}/`)[1] ?? '')
    : ''
  const isSettings =
    networkSubPath === 'settings' || networkSubPath.startsWith('settings/')
  // Client-wide sections (teams, users…) have no geographic preview to scope.
  const isClientScopedSettings = !!findSettingsEntry(pathname)?.clientScoped

  // Desktop-only project controls that live in the global header. The calendar
  // shows on the time-based project screens; the sector filter is limited to
  // the activities list. The project picker is on the linear (work) view and on
  // settings, whose project-scoped sections are meaningless without knowing —
  // and being able to change — which project they belong to.
  // `currentSubPath` is only the last URL segment, so a settings section whose
  // name collides with a calendar sub-path (e.g. `settings/protections` vs the
  // `protections` network tab) would wrongly show the temporal cluster.
  // Settings never has a date to navigate — exclude it explicitly.
  const showCalendar =
    isInNetwork &&
    !isMobile &&
    !isSettings &&
    HEADER_CALENDAR_SUB_PATHS.has(currentSubPath)
  const showProjectSelector =
    isInNetwork && !isMobile && (currentSubPath === 'work' || isSettings)
  const showSectorSelector =
    isInNetwork &&
    !isMobile &&
    (currentSubPath === 'activities' || currentSubPath === 'notices')
  // The linear views (OPC work + admin network inspector) designate their PK
  // window here — sector filter + PK bounds — so the axis keeps every vertical
  // pixel in the screen's own header. The ruler step stays in that header.
  const isLinearView =
    isInNetwork &&
    !isMobile &&
    (currentSubPath === 'work' || currentSubPath === 'network')
  const linearView: TLinearView =
    currentSubPath === 'network' ? 'network' : 'work'

  // List "period" (date-range) view, shared by the activities, notices and
  // reports tabs. The view switch gains a period segment on those screens; when
  // period is active, the week/day navigator (arrows + today) is meaningless, so
  // it is replaced by the range picker. Other screens are unaffected.
  const isListView =
    currentSubPath === 'activities' ||
    currentSubPath === 'notices' ||
    currentSubPath === 'reports'
  // The resource calendar always displays a fixed two-week window derived from
  // the week of the selected date, so it locks the navigator to week mode and
  // drops the day/week switch, which has nothing to act on there.
  const isResourceCalendar = currentSubPath === 'resource-calendar'
  // The synoptic draws one day of the network, so it locks the navigator to day
  // mode and shows the day segment alone: the shared `viewMode` is left as the
  // user set it for the other tabs.
  const isSynoptic = currentSubPath === 'synoptic'
  const forcedViewMode = isResourceCalendar
    ? 'week'
    : isSynoptic
      ? 'day'
      : undefined
  const dateMode = useListDateMode()
  const isPeriodMode = isListView && dateMode === 'period'
  const {value: periodValue, onPickRange} = useListPeriodPicker()

  // The network dashboard designates its period + shift here too — its own
  // filter store, distinct from the shared project-view filters. Desktop-only,
  // like every other header control cluster.
  const showDashboardControls =
    isInNetwork && !isMobile && currentSubPath === 'dashboard'
  const dashboardFilters = useDashboardHeaderFilters()

  const {t} = useTranslation()

  let client
  if (clientId) {
    client = data?.find(ele => ele.id === clientId)
  }
  const logo_client = client?.logo[0]?.url
    ? `${CONFIG.apiUrl}/${client?.logo[0]?.url}`
    : logo_project

  const onClickLogout = () => {
    AuthService.logout()
    navigate('/auth/login')
  }

  const onClickLogo = () => {
    navigate('/')
  }
  const onClickClient = () => {
    if (clientId) {
      navigate(`/clients/${clientId}`)
    }
  }
  return (
    <div className='headerContainer'>
      <div className='logoWrapper'>
        <button className='ArrowButton' onClick={onClickLogo}>
          <img src={logo_tops} className='headerLogoImage' alt='logo' />
        </button>
        {!!clientId && <Separator variant='structural' />}
        {!!clientId && (
          <button className='ArrowButton' onClick={onClickClient}>
            <img
              src={logo_client}
              className='headerLogoImage'
              alt='logo_client'
            />
          </button>
        )}
        {(showCalendar ||
          showProjectSelector ||
          isLinearView ||
          showDashboardControls) && (
          // Project-context controls, desktop-only. The calendar drives the
          // selected date/week; the sector filter belongs to the activities
          // list. Settings takes the project picker WITHOUT the temporal
          // cluster — it has no date to navigate.
          <div className='headerProjectControls'>
            <Separator variant='structural' />
            {/* Dashboard period + shift, bound to the dashboard filter store. */}
            {showDashboardControls && (
              <div className='temporalControls'>
                <DashboardPeriodPicker
                  value={dashboardFilters.periodValue}
                  onChange={dashboardFilters.onChangePeriod}
                  label={t('dashboard:periodLabel')}
                />
                <ShiftFilterBase
                  value={dashboardFilters.selectedShift}
                  onChange={dashboardFilters.onChangeShift}
                />
              </div>
            )}
            {/* Temporal controls: view mode + week/day navigator + today +
                shift. In period mode the navigator is swapped for the range
                picker, but the shift switch still applies. */}
            {showCalendar && (
              <div className='temporalControls'>
                {!isResourceCalendar && !isSynoptic && (
                  <ViewModeSwitch showPeriod={isListView} />
                )}
                {isSynoptic && (
                  <ButtonGroup>
                    <IconButton
                      icon='hours24'
                      iconSize='24px'
                      iconColor={theme.palette.primary.main}
                      active
                      tooltip={t('tooltip:dayView')}
                    />
                  </ButtonGroup>
                )}
                {isPeriodMode ? (
                  <DashboardPeriodPicker
                    value={periodValue}
                    onChange={onPickRange}
                    label={t('activityList:period')}
                    autoOpenWhenEmpty
                  />
                ) : (
                  <>
                    <TodayButton forceViewMode={forcedViewMode} />
                    <CalendarHeader forceViewMode={forcedViewMode} />
                  </>
                )}
                {/* The resource calendar always shows both shifts (each row is
                  split night/day), so it has no shift to switch. */}
                {!isResourceCalendar && <ShiftFilter />}
              </div>
            )}
            {/* Divide the temporal cluster from the place/scope filters — only
                when there IS a temporal cluster to divide from. */}
            {showCalendar && (showSectorSelector || isLinearView) && (
              <Separator variant='inset' />
            )}
            {/* The plain sector filter serves the activities and notices
                lists. The reports matrix does not honour it, so it is hidden
                there rather than shown inert. */}
            {showSectorSelector && <SectorFilter />}
            {/* Linear view window source: one popover, sector OR PK bounds
                (mutually exclusive). The ruler step lives in the screen's own
                header. Settings reuses the same control to scope its geographic
                preview to a sector/PK window. */}
            {(isLinearView || (isSettings && !isClientScopedSettings)) && (
              <LinearWindowFilter view={isSettings ? 'settings' : linearView} />
            )}
          </div>
        )}
      </div>
      <div className='headerAction'>
        <div className='roleText'>{`${user?.firstName} ${user?.lastName}`}</div>
        <button className='ArrowButton' onClick={onClickLogout}>
          <Icon type='logout' size='22px' tooltip={t('general:logout')} />
        </button>
      </div>
    </div>
  )
}

export default Header
